/* Suppress only ReelUp floating promos on product pages in the CandyPeak draft. */
(() => {
  'use strict';
  const body = document.body;
  if (!body || !body.classList.contains('cp-harmonie') || !body.classList.contains('template-product') || window.cpProductReelPopsGuard) return;
  window.cpProductReelPopsGuard = true;
  const selector = '#reelUp_pops_root';
  const watched = new WeakSet();

  function quietVideo(video) {
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.preload = 'none';
    if (!video.paused) video.pause();
  }

  function hideRoot(root) {
    if (!root.hidden) root.hidden = true;
    if (!root.inert) root.inert = true;
    if (root.getAttribute('aria-hidden') !== 'true') root.setAttribute('aria-hidden', 'true');
  }

  function quietRoot(root) {
    hideRoot(root);
    root.querySelectorAll('video').forEach(quietVideo);
    if (watched.has(root)) return;
    watched.add(root);
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'attributes') { hideRoot(root); continue; }
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches('video')) quietVideo(node);
          node.querySelectorAll('video').forEach(quietVideo);
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'inert', 'aria-hidden'] });
  }

  // Capture non-bubbling play events before app code can resume a floating promo.
  document.addEventListener('play', event => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !video.closest(selector)) return;
    quietVideo(video);
  }, true);

  document.querySelectorAll(selector).forEach(quietRoot);
  // App embeds may mount or replace their root after the product has rendered.
  // Inspect only added element subtrees; never scan the complete document per mutation.
  const mountObserver = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches(selector)) quietRoot(node);
        node.querySelectorAll(selector).forEach(quietRoot);
      }
    }
  });
  mountObserver.observe(body, { childList: true, subtree: true });
})();

/* Play only the visible product slide. Independent of thumbnail navigation. */
(() => {
  'use strict';
  if (window.cpProductGalleryLoopsReady) return;
  window.cpProductGalleryLoopsReady = true;
  const controllers = new Map();
  let frame;
  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => {
      frame = null;
      controllers.forEach(controller => controller.update());
    });
  }
  function configure(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.controls = false;
    video.autoplay = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');
  }
  function command(iframe, play) {
    let origin;
    try { origin = new URL(iframe.src).origin; } catch (_) { return; }
    if (/youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname)) {
      for (const func of play ? ['mute', 'playVideo'] : ['pauseVideo']) {
        iframe.contentWindow?.postMessage(JSON.stringify({event:'command', func, args:[]}), origin);
      }
    } else if (new URL(origin).hostname === 'player.vimeo.com') {
      if (play) iframe.contentWindow?.postMessage(JSON.stringify({method:'setVolume', value:0}), origin);
      iframe.contentWindow?.postMessage(JSON.stringify({method:play ? 'play' : 'pause'}), origin);
    }
  }
  function mount(gallery) {
    if (controllers.has(gallery) || !gallery.querySelector('[data-cp-loop-media]')) return;
    const list = gallery.querySelector('.product__media-list');
    if (!list) return;
    const io = new IntersectionObserver(schedule, {threshold:[0, .15, .5, .65, 1]});
    const observed = new Set();
    const controller = {
      update() {
        if (!gallery.isConnected) {
          io.disconnect(); observer.disconnect(); resize.disconnect();
          list.removeEventListener('scroll', schedule); controllers.delete(gallery); return;
        }
        const items = Array.from(list.children).filter(item => item.hasAttribute('data-media-id'));
        items.forEach(item => { if (!observed.has(item)) { observed.add(item); io.observe(item); } });
        const bounds = list.getBoundingClientRect();
        const blocked = document.hidden || document.querySelector('cart-drawer.active, quick-add-modal[open], product-modal[open], details.menu-drawer-container[open]');
        let active = null, best = 0;
        if (!blocked) items.forEach(item => {
          const rect = item.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const horizontal = Math.max(0, Math.min(rect.right, bounds.right, innerWidth) - Math.max(rect.left, bounds.left, 0));
          const vertical = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
          const ratio = horizontal / Math.min(rect.width, bounds.width);
          if (ratio >= .6 && vertical >= Math.min(100, rect.height * .25)) {
            const score = ratio * vertical;
            if (score > best) { best = score; active = item; }
          }
        });
        gallery.querySelectorAll('[data-cp-loop-media]').forEach(deferred => {
          const play = !!active && active.contains(deferred);
          let media = deferred.querySelector(':scope > video, :scope > iframe');
          if (play && !media) {
            media = deferred.querySelector('template')?.content.querySelector('video,iframe')?.cloneNode(true);
            if (!media) return;
            if (media.tagName === 'VIDEO') configure(media);
            else {
              media.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
              media.addEventListener('load', schedule);
            }
            deferred.setAttribute('loaded', 'true');
            deferred.appendChild(media);
          }
          if (!media) return;
          if (media.tagName === 'VIDEO') {
            configure(media);
            if (play && media.paused) media.play()?.catch(() => {});
            else if (!play && !media.paused) media.pause();
          } else command(media, play);
        });
      }
    };
    const observer = new MutationObserver(records => {
      if (records.some(record => record.type === 'childList' || record.attributeName === 'class')) schedule();
    });
    observer.observe(gallery, {childList:true, subtree:true, attributes:true, attributeFilter:['class']});
    const resize = new ResizeObserver(schedule); resize.observe(list);
    list.addEventListener('scroll', schedule, {passive:true});
    gallery.addEventListener('slideChanged', schedule);
    controllers.set(gallery, controller);
  }
  function init() { document.querySelectorAll('media-gallery').forEach(mount); schedule(); }
  init();
  new MutationObserver(init).observe(document.querySelector('#MainContent') || document.body, {childList:true, subtree:true});
  new MutationObserver(schedule).observe(document.body, {attributes:true, attributeFilter:['class']});
  for (const event of ['scroll', 'resize', 'pageshow']) window.addEventListener(event, schedule, {passive:true});
  for (const event of ['visibilitychange', 'pointerup', 'touchend', 'click', 'modalClosed']) document.addEventListener(event, schedule, {passive:true, capture:true});
})();
